import { isTransitionAllowed, type IssueMode, type IssueStatus } from '../types/issue';
import type { IssueNextAction } from './issueNextAction';

export type WorkspaceToolbar =
  | { kind: 'legacy' }
  | { kind: 'hidden' }
  | { kind: 'assign' }
  | { kind: 'closure'; label: string }
  | { kind: 'transition'; label: string; targetStatus: IssueStatus };

/**
 * O botão azul só aparece para transição, atribuição ou motivo de encerramento.
 * Foco, rolagem e o título do trabalho que já está aberto não geram CTA.
 *
 * A máquina permite `action_plan → execution` e `execution → validation` sem
 * contramedida. No Problema Estruturado esses dois CTAs ficam só na UI:
 * Execução exige ao menos uma contramedida direta não cancelada; Verificação
 * exige que todas as não canceladas estejam concluídas. 5W2H não entra no gate.
 * A máquina também permite `validation → closed` e `monitoring → closed` sem
 * padronização. No Problema Estruturado, Encerrar problema só aparece em
 * `monitoring`, com o caso B do P4 e os quatro registros de padronização.
 */
export function resolveWorkspaceToolbar(input: {
  quick: boolean;
  mode: IssueMode;
  status: IssueStatus;
  causeConfirmed: boolean;
  nextAction: IssueNextAction;
  activeCountermeasures?: number;
  completedCountermeasures?: number;
  /** Tentativa P4 effective que cobre todas as contramedidas concluídas. Medição não entra. */
  efficacyAllowsStandardization?: boolean;
  /** Eficácia P4 ainda válida e os quatro itens de padronização registrados. */
  standardizationAllowsConclusion?: boolean;
}): WorkspaceToolbar {
  if (input.quick) return { kind: 'legacy' };

  const { mode, status, causeConfirmed, nextAction } = input;
  const activeCountermeasures = input.activeCountermeasures ?? 0;
  const completedCountermeasures = input.completedCountermeasures ?? 0;
  if (
    causeConfirmed
    && (status === 'investigation' || status === 'root_cause')
    && isTransitionAllowed(mode, status, 'action_plan')
  ) {
    return { kind: 'transition', label: 'Avançar para Plano de ação', targetStatus: 'action_plan' };
  }

  if (
    status === 'action_plan'
    && activeCountermeasures >= 1
    && isTransitionAllowed(mode, status, 'execution')
  ) {
    return { kind: 'transition', label: 'Avançar para Execução', targetStatus: 'execution' };
  }

  if (status === 'execution') {
    if (
      activeCountermeasures >= 1
      && completedCountermeasures === activeCountermeasures
      && isTransitionAllowed(mode, status, 'validation')
    ) {
      return { kind: 'transition', label: 'Avançar para Verificação', targetStatus: 'validation' };
    }
    return { kind: 'hidden' };
  }

  if (status === 'validation') {
    if (
      input.efficacyAllowsStandardization
      && isTransitionAllowed(mode, status, 'monitoring')
    ) {
      return { kind: 'transition', label: 'Avançar para Padronização', targetStatus: 'monitoring' };
    }
    return { kind: 'hidden' };
  }

  if (status === 'monitoring') {
    if (
      input.standardizationAllowsConclusion
      && input.efficacyAllowsStandardization
      && isTransitionAllowed(mode, status, 'closed')
    ) {
      return { kind: 'transition', label: 'Encerrar problema', targetStatus: 'closed' };
    }
    return { kind: 'hidden' };
  }

  if (status === 'closed') return { kind: 'hidden' };

  const surface = status === 'open' || status === 'investigation' || status === 'root_cause' || status === 'action_plan';
  if (!surface) return { kind: 'legacy' };
  if (nextAction.kind === 'assign') return { kind: 'assign' };
  if (nextAction.key === 'closure_reason') return { kind: 'closure', label: nextAction.label };
  if (
    nextAction.kind === 'advance_status'
    && nextAction.targetStatus
    && isTransitionAllowed(mode, status, nextAction.targetStatus)
  ) {
    return { kind: 'transition', label: nextAction.label, targetStatus: nextAction.targetStatus };
  }
  return { kind: 'hidden' };
}
